import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

export default function OrdenesEnvio() {
  const [ordenes, setOrdenes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openTracking, setOpenTracking] = useState(false);
  const [ordenActual, setOrdenActual] = useState(null);

  useEffect(() => {
    cargarOrdenes();
  }, []);

  const cargarOrdenes = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.LOGISTICA.ORDENES);
      setOrdenes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al cargar órdenes:", error);
      setOrdenes([]);
    }
  };

  const handleOpenTracking = (orden) => {
    setOrdenActual(orden);
    setOpenTracking(true);
  };

  const getEstadoChip = (estado) => {
    const estados = {
      pendiente: { color: "secondary", label: "Pendiente", icon: "schedule" },
      preparacion: { color: "info", label: "En Preparación", icon: "inventory" },
      en_transito: { color: "warning", label: "En Tránsito", icon: "local_shipping" },
      entregado: { color: "success", label: "Entregado", icon: "check_circle" },
      cancelado: { color: "error", label: "Cancelado", icon: "cancel" },
    };
    const est = estados[estado] || estados.pendiente;
    return (
      <Chip
        icon={<Icon fontSize="small">{est.icon}</Icon>}
        label={est.label}
        color={est.color}
        size="small"
      />
    );
  };

  const getPrioridadChip = (prioridad) => {
    const prioridades = {
      baja: { color: "default", label: "Baja" },
      media: { color: "info", label: "Media" },
      alta: { color: "warning", label: "Alta" },
      urgente: { color: "error", label: "Urgente" },
    };
    const pri = prioridades[prioridad] || prioridades.media;
    return <Chip label={pri.label} color={pri.color} size="small" variant="outlined" />;
  };

  const getEstadoStep = (estado) => {
    const steps = ["Pendiente", "En Preparación", "En Tránsito", "Entregado"];
    const activeSteps = {
      pendiente: 0,
      preparacion: 1,
      en_transito: 2,
      entregado: 3,
    };
    return activeSteps[estado] || 0;
  };

  /* eslint-disable react/prop-types */
  const columns = [
    { Header: "Código", accessor: "codigo", width: "12%" },
    { Header: "Cliente", accessor: "cliente", width: "18%" },
    { Header: "Dirección", accessor: "direccion", width: "20%" },
    {
      Header: "Carga",
      accessor: "carga",
      width: "10%",
      Cell: ({ row }) => (
        <MDBox>
          <MDTypography variant="caption" display="block">
            {row.original.peso} kg
          </MDTypography>
          <MDTypography variant="caption" color="text">
            {row.original.volumen} m³
          </MDTypography>
        </MDBox>
      ),
    },
    { Header: "Fecha", accessor: "fecha_programada", width: "10%" },
    {
      Header: "Prioridad",
      accessor: "prioridad",
      width: "8%",
      Cell: ({ value }) => getPrioridadChip(value),
    },
    {
      Header: "Estado",
      accessor: "estado",
      width: "12%",
      Cell: ({ value }) => getEstadoChip(value),
    },
    {
      Header: "Acciones",
      accessor: "acciones",
      width: "10%",
      Cell: ({ row }) => (
        <MDBox display="flex" gap={1}>
          <Tooltip title="Tracking">
            <IconButton size="small" color="info" onClick={() => handleOpenTracking(row.original)}>
              <Icon fontSize="small">map</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Imprimir Guía">
            <IconButton size="small" color="primary">
              <Icon fontSize="small">print</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" color="warning">
              <Icon fontSize="small">edit</Icon>
            </IconButton>
          </Tooltip>
        </MDBox>
      ),
    },
  ];
  /* eslint-enable react/prop-types */

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDTypography variant="h5">Órdenes de Envío</MDTypography>
        <MDButton variant="gradient" color="info" startIcon={<Icon>add</Icon>}>
          Nueva Orden
        </MDButton>
      </MDBox>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="secondary">
                schedule
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {ordenes.filter((o) => o.estado === "pendiente").length}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Pendientes
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="warning">
                local_shipping
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {ordenes.filter((o) => o.estado === "en_transito").length}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                En Tránsito
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="success">
                check_circle
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {ordenes.filter((o) => o.estado === "entregado").length}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Entregadas Hoy
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="info">
                inventory
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {ordenes.length}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Total de Órdenes
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <DataTable
          table={{ columns, rows: ordenes }}
          showTotalEntries={true}
          isSorted={true}
          noEndBorder
          entriesPerPage={false}
          canSearch
        />
      </Card>

      {/* Dialog de Tracking */}
      <Dialog open={openTracking} onClose={() => setOpenTracking(false)} maxWidth="md" fullWidth>
        <DialogTitle>Tracking de Envío - {ordenActual?.codigo}</DialogTitle>
        <DialogContent>
          {ordenActual && (
            <MDBox>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={6}>
                  <MDTypography variant="button" fontWeight="medium">
                    Cliente:
                  </MDTypography>
                  <MDTypography variant="body2">{ordenActual.cliente}</MDTypography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDTypography variant="button" fontWeight="medium">
                    Dirección:
                  </MDTypography>
                  <MDTypography variant="body2">{ordenActual.direccion}</MDTypography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDTypography variant="button" fontWeight="medium">
                    Conductor:
                  </MDTypography>
                  <MDTypography variant="body2">
                    {ordenActual.conductor || "No asignado"}
                  </MDTypography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDTypography variant="button" fontWeight="medium">
                    Vehículo:
                  </MDTypography>
                  <MDTypography variant="body2">
                    {ordenActual.vehiculo || "No asignado"}
                  </MDTypography>
                </Grid>
              </Grid>

              <Stepper activeStep={getEstadoStep(ordenActual.estado)} sx={{ mb: 3 }}>
                <Step>
                  <StepLabel>Pendiente</StepLabel>
                </Step>
                <Step>
                  <StepLabel>En Preparación</StepLabel>
                </Step>
                <Step>
                  <StepLabel>En Tránsito</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Entregado</StepLabel>
                </Step>
              </Stepper>

              <MDTypography variant="h6" mb={2}>
                Historial de Tracking
              </MDTypography>
              {ordenActual.tracking.map((evento, index) => (
                <Card key={index} sx={{ mb: 1, p: 2 }}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center">
                    <MDBox>
                      <MDTypography variant="button" fontWeight="medium">
                        {evento.evento}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        <Icon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }}>
                          location_on
                        </Icon>
                        {evento.ubicacion}
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="caption" color="text">
                      {evento.fecha}
                    </MDTypography>
                  </MDBox>
                </Card>
              ))}
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTracking(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
